import React, { useState, useEffect, useMemo } from "react";
import { Row, Card, Col, Spin, message } from "antd";
import { useDispatch } from "react-redux";
import { LoadingOutlined } from "@ant-design/icons";
import moment from "moment";
import { useKeycloak } from "@react-keycloak/web";

import Headings from "../../atoms/Headigs";
import FigureChip from "../../atoms/FigureChip";
import {
  DashboardActivityIcon,
  DashboardNewAccountIcon,
} from "../../atoms/CustomIcons";
import DestinationCountryReport from "./components/DestinationCountryReport";
import ProjectedInvoiceCard from './components/ProjectedInvoiceCard';
import BandwidthConsumeChart from './components/BandwidthChart';
import ConnectedProtocolsChart from './components/ConnectedProtocolsChart';
import ConnectedUsersChart from './components/ConnectedUsersChart';
import Banner from './components/Banner';
import { getDashboardSummary, getResellerAccActivity } from "./ducks/services";
import { getAllResellersProtocols } from "../../Pages/Dashboard/ducks/actions";
import { setUserData } from "Modules/Login/ducks/action";

const antIcon = <LoadingOutlined spin />;

const Dashboard = () => {
  const dispatch = useDispatch();
  const { keycloak } = useKeycloak();

  const [loading, setLoading] = useState({
    userActivity: 0,
    resellerAcc: 0,
    protocols: true,
    bandwidth: true,
    destinationCountry: true,
    connectedUsers: true,
  });

  const [resellerAccActivity, setResellerAccActivity] = useState({});
  const [protocolData, setProtocolData] = useState([]);
  const [bandwidthData, setBandwidthData] = useState([]);
  const [destinationCountryData, setDestinationCountryData] = useState([]);
  const [connectedUsers, setConnectedUsers] = useState(0);
  const [ringPercent, setRingPercent] = useState(null);
  const [userActivity, setUserActivity] = useState(0);
  const [showBandwidthChart, setShowBandwidthChart] = useState(false);

  const updateLoading = (key, value = false) =>
    setLoading((prev) => ({ ...prev, [key]: value }));

  const fetchResellerActivity = async () => {
    try {
      const res = await getResellerAccActivity();
      if (res?.data?.header?.code === 200) {
        setResellerAccActivity(res.data.body);
      } else {
        message.error("Reseller activity fetch error");
      }
    } catch (err) {
      message.error(err?.response?.data?.header?.message || "Failed to fetch reseller activity");
    } finally {
      updateLoading("resellerAcc");
    }
  };

  const fetchDashboardData = async () => {
    try {
      const summary = await getDashboardSummary(keycloak.token);
      const body = summary.data?.body || {};

      setDestinationCountryData(body.destinationCountry || []);
      updateLoading("destinationCountry");

      setProtocolData(body.protocolStats || []);
      updateLoading("protocols");

      const userCount = body.connectedVpnUsersConnectionInfo?.distinctUserCount || 0;
      setConnectedUsers(userCount);

      const lastActivity = body.connectedVpnUsersConnectionInfo?.lastActivity;
      if (lastActivity) {
        const hoursAgo = Math.floor((Date.now() - new Date(lastActivity)) / (1000 * 60 * 60));
        setUserActivity(hoursAgo);
      } else {
        setUserActivity(0);
      }
      updateLoading("userActivity");

      if ('bandwidthChart' in body) {
        setBandwidthData(Array.isArray(body.bandwidthChart) ? body.bandwidthChart : []);
        setShowBandwidthChart(true); 
      } else {
        setBandwidthData([]);
        setShowBandwidthChart(false); 
      }
      updateLoading("bandwidth");

    } catch (err) {
      console.error("Dashboard fetch failed", err);
      message.error("Failed to fetch dashboard data");
      updateLoading("userActivity");
      updateLoading("protocols");
      updateLoading("bandwidth");
      updateLoading("destinationCountry");
    }
  };

  useEffect(() => {
    dispatch(setUserData());
    dispatch(getAllResellersProtocols());
    fetchResellerActivity();
    fetchDashboardData();
  }, [dispatch]);

  // Compute ring percentage after both connectedUsers & resellerAccActivity are fetched
  useEffect(() => {
    const totalRecords = Number(resellerAccActivity.total_records?.replace(/,/g, "") || 0);
    if (!isNaN(totalRecords) && totalRecords > 0) {
      setRingPercent(connectedUsers / totalRecords);
    } else {
      setRingPercent(0); 
    }
  
    updateLoading("connectedUsers"); 
  }, [resellerAccActivity, connectedUsers]);

  const mappedProtocolData = useMemo(
    () => protocolData.map(d => ({ type: d.TunnelTypeName, value: d.TotalUsers })),
    [protocolData]
  );

  const filteredProtocolData = useMemo(
    () => mappedProtocolData.filter(d => d.type !== "Online"),
    [mappedProtocolData]
  );

  const getYearToDateRange = () => [
    moment().startOf('year').format('YYYY-MM-DD'),
    moment().format('YYYY-MM-DD')
  ];

return (
  <>
    <Banner />
    <Card className="design-card">
      <Row gutter={[15, 15]}>
        {/* <Col span={24}>
          <Breadcrumb>
            <Breadcrumb.Item>Disrupt</Breadcrumb.Item>
            <Breadcrumb.Item>Dashboard</Breadcrumb.Item>
          </Breadcrumb>
        </Col> */}
        <Col span={24}>
          <Headings
            title="VPN Partner Console"
            titleSize={2}
            description={`Dynamic view of your organization's active sessions, licenses`}
            space={5}
            descClass="mb-0 c-gray"
          />
        </Col>
        <Col span={8}>
          <FigureChip
            icon={<DashboardActivityIcon style={{ fontSize: '18px' }} />}
            stats={loading.userActivity ? <Spin indicator={antIcon} size="small" /> : `${userActivity}`}
            statsTitle=" Hours ago"
            description="Last Activity"
            hoverable={false}
            redirectUrl={false}
          />
        </Col>
        <Col span={8}>
          <FigureChip
            icon={<DashboardNewAccountIcon />}
            stats={loading.resellerAcc ? <Spin indicator={antIcon} size="small" /> : `${resellerAccActivity?.users_in_interval}`}
            statsTitle=""
            description="Last Created Accounts"
            hoverable={false}
            redirectUrl={false}
          />
        </Col>
        <Col span={8}>
          <FigureChip
            icon={<DashboardNewAccountIcon />}
            stats={loading.resellerAcc ? <Spin indicator={antIcon} size="small" /> : `${resellerAccActivity?.total_records}`}
            statsTitle=""
            description="Total Accounts"
            hoverable={true}
            redirectUrl="/vpn-users"
          />
        </Col>
        <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ height: '100%' }}>
          <DestinationCountryReport data={destinationCountryData} />
        </Col>
         
          {/* <Row gutter={[0, 24]}>
            <Col span={12}>
              <ConnectedProtocolsChart data={protocolData} loading={loading.protocols} />
            </Col>
            <Col span={12}>
              <ProjectedInvoiceCard />
            </Col>
          </Row> */}
        <Col xs={24} sm={12} md={6} lg={6} xl={6} style={{ height: '100%' }}>
           <ConnectedProtocolsChart data={filteredProtocolData} loading={loading.protocols} cardStyle={{ height: '100%' }} />
        </Col>
        <Col xs={24} sm={12} md={6} lg={6} xl={6} style={{ height: '100%' }}>
          <ProjectedInvoiceCard cardStyle={{ height: '100%' }} />
        </Col>
        {showBandwidthChart && (
          <Col span={12}>
            <BandwidthConsumeChart data={bandwidthData} loading={loading.bandwidth} />
          </Col>
        )}
        <Col span={6}>
          <ConnectedUsersChart percent={ringPercent ?? 0} loading={loading.connectedUsers} />
        </Col>
      </Row>
    </Card>
  </>
);
};
export default Dashboard;
